import { formatNumber } from "@kvix/shared";
import { Box, IconButton, LinearProgress, makeStyles } from "@material-ui/core";
import StarIcon from "@material-ui/icons/Star";
import classNames from "classnames";
import React, { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { KvixIcon } from "../../../icons";
import { Text } from "../../../Primitives";
import { UserAvatarListCards } from "../../../User";
const useStyles = makeStyles((theme) => ({
    root: {
        position: "relative",
        height: (props) => (props.wide ? "auto" : 356),
        width: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
        cursor: "pointer",
        paddingTop: (props) => (props.wide ? "56.25%" : 0),
        borderRadius: "6px",
        [theme.breakpoints.down("sm")]: {
            height: (props) => (props.wide ? "auto" : 356 / 1.3)
        },
        "&.square": {
            height: 246,
            borderRadius: 6,
            [theme.breakpoints.down("xs")]: {
                height: 246 / 1.3
            }
        },
        "&:hover": {
            "& > div:first-of-type": {
                transform: "scale(1.07)",
                [theme.breakpoints.down("md")]: {
                    transform: "none"
                }
            }
        }
    },
    background: {
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        position: "absolute",
        width: "100%",
        height: "100%",
        transition: "transform 600ms ease-in-out",
        "&.wide": {
            top: 0,
            left: 0
        },
        "&.imageLoader": {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            "&:after": {
                content: "' '",
                display: "block",
                width: 44,
                height: 44,
                margin: 6,
                borderRadius: "50%",
                border: `4px solid ${theme.palette.primary.main}`,
                borderColor: `${theme.palette.primary.main} transparent ${theme.palette.primary.main} transparent`,
                animation: "$imageLoader 1.2s linear infinite"
            }
        },
        "&.imageFadeIn": {
            animation: "$fadeIn 400ms linear"
        }
    },
    textContainer: {
        display: "flex",
        flexDirection: "column",
        padding: "15px",
        height: "100%",
        width: "100%",
        justifyContent: "flex-end",
        userSelect: "none",
        position: "absolute",
        textAlign: "start",
        bottom: 0,
        left: 0,
        [theme.breakpoints.down("sm")]: {
            padding: `${15 / 1.3}px ${15 / 1.3}px`
        }
    },
    cardText: {
        color: theme.palette.common.white,
        wordBreak: "break-word",
        zIndex: 1,
        "&.instructorName": {
            [theme.breakpoints.down("sm")]: {}
        },
        "&.title": {
            marginBottom: 4,
            width: "80%",
            [theme.breakpoints.down("sm")]: {
                width: "89%"
            }
        },
        "&.partnerTitle": {
            margin: "0 0 0px 0px",
            position: "relative"
        },
        "&.date": {
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: "-0.25px"
        }
    },
    descriptionAndRating: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        color: theme.palette.common.white,
        zIndex: 1
    },
    rating: {
        display: "flex",
        alignItems: "center",
        fontWeight: 700
    },
    ratingIcon: {
        marginLeft: 2,
        "&.MuiSvgIcon-root": {
            fill: "#fecb00"
        }
    },
    favouriteButton: {
        position: "absolute",
        top: 0,
        right: 0,
        color: theme.palette.primary.main,
        textTransform: "initial",
        background: "transparent",
        width: "28px",
        height: "28px",
        boxShadow: "none",
        transition: "transform 200ms ease-in-out",
        "&:hover": {
            transform: "scale(1.1)",
            background: "transparent"
        },
        "&:focus": {
            boxShadow: "none"
        }
    },
    heartIcon: {
        marginTop: "2px",
        fontSize: 22,
        zIndex: 1
    },
    timeIndicator: {
        position: "absolute",
        top: 2,
        left: 2,
        background: theme.palette.common.white,
        color: theme.palette.common.black,
        fontSize: 13,
        lineHeight: 12,
        letterSpacing: "0.25px",
        fontWeight: 700,
        height: "20px",
        paddingLeft: "3px",
        paddingRight: "3px",
        borderRadius: "4px",
        zIndex: 1,
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "center",
        "&.live": {
            fontWeight: 700,
            background: theme.palette.primary.main,
            color: theme.palette.common.white,
            textTransform: "uppercase"
        },
        "&.cancelled": {
            textDecoration: "line-through"
        }
    },
    followers: {
        position: "absolute",
        top: 4,
        right: 8,
        color: theme.palette.common.black,
        textAlign: "center",
        "& > div:first-of-type": {
            fontWeight: 600
        }
    },
    progressBar: {
        position: "absolute",
        bottom: 0,
        left: 0,
        width: "100%",
        "&.MuiLinearProgress-colorPrimary": {
            background: "transparent"
        }
    },
    finishedOverlay: {
        position: "absolute",
        width: "100%",
        height: "100%",
        left: 0,
        bottom: 0,
        backgroundColor: theme.palette.common.black,
        opacity: 0.5
    },
    "@keyframes fadeIn": {
        "100%": {
            opacity: 1
        },
        "0%": {
            opacity: 0
        }
    },
    "@keyframes imageLoader": {
        "0%": {
            transform: "rotate(0deg)"
        },
        "100%": {
            transform: "rotate(360deg)"
        }
    }
}));
export const CardBase = (props) => {
    const { t, ready } = useTranslation("cards");
    const classes = useStyles(props);
    const background1Ref = useRef();
    const background2Ref = useRef();
    const image = new Image();
    const { title, instructorName, link, toggleFavourite, isLiked, isLive, playButton, wasLiveAt, goesLiveAt, noOfFollowers, imageUrl, thumbnailUrl, partnerTitle, wide } = props;
    const loadImage = useCallback((ref, imageUrl) => {
        if (ref.current) {
            ref.current.classList.remove("imageLoader");
            ref.current.style.backgroundImage = `linear-gradient(25deg, rgba(31,40,51,0.9) 20%, rgba(31,40,51,0.06) 55%), url(${imageUrl})`;
            if (!isLive) {
                ref.current.classList.add("imageFadeIn");
            }
        }
    }, []);
    useEffect(() => {
        var _a, _b;
        if (isLive && wide) {
            image.src = thumbnailUrl || imageUrl;
            if (((_b = (_a = background1Ref.current) === null || _a === void 0 ? void 0 : _a.style) === null || _b === void 0 ? void 0 : _b.visibility) === "hidden") {
                image.onload = () => {
                    var _a, _b, _c, _d;
                    loadImage(background1Ref, image.src);
                    if (((_b = (_a = background2Ref === null || background2Ref === void 0 ? void 0 : background2Ref.current) === null || _a === void 0 ? void 0 : _a.style) === null || _b === void 0 ? void 0 : _b.visibility) &&
                        ((_d = (_c = background1Ref === null || background1Ref === void 0 ? void 0 : background1Ref.current) === null || _c === void 0 ? void 0 : _c.style) === null || _d === void 0 ? void 0 : _d.visibility)) {
                        background1Ref.current.style.visibility = "visible";
                        background2Ref.current.style.visibility = "hidden";
                    }
                };
            }
            else {
                image.onload = () => {
                    loadImage(background2Ref, image.src);
                    if (background2Ref.current && background1Ref.current) {
                        background2Ref.current.style.visibility = "visible";
                        background1Ref.current.style.visibility = "hidden";
                    }
                };
            }
        }
        else {
            image.src = imageUrl;
            const fallbackTimeout = setTimeout(() => {
                image.onload = null;
                loadImage(background1Ref, image.src);
            }, 5000);
            image.onload = () => {
                if (background1Ref.current) {
                    clearTimeout(fallbackTimeout);
                    loadImage(background1Ref, image.src);
                }
            };
            return () => {
                clearTimeout(fallbackTimeout);
            };
        }
    }, [image, imageUrl, isLive, wide, background1Ref, background2Ref]);
    if (!ready) {
        return null;
    }
    return (React.createElement(Link, { to: link },
        React.createElement("div", { className: `${classes.root} ${props.instructorLayout ? "square" : ""}` },
            React.createElement("div", { className: classNames(classes.background, {
                    imageLoader: true,
                    wide
                }), ref: background1Ref }),
            React.createElement("div", { className: classNames(classes.background, {
                    imageLoader: true,
                    wide
                }), style: { visibility: "hidden" }, ref: background2Ref }),
            goesLiveAt && (React.createElement(React.Fragment, null, props.isCancelled ? (React.createElement(Text, { variant: "body2", className: `${classes.timeIndicator}` },
                React.createElement("span", { style: { textDecoration: "line-through", marginRight: 4 } }, goesLiveAt),
                React.createElement("span", null, t("isCancelled")))) : props.isStarting ? (React.createElement(Text, { variant: "body2", className: `${classes.timeIndicator}` }, t("isStarting"))) : props.isLive ? (React.createElement(Text, { variant: "body2", className: `${classes.timeIndicator} live` }, t("isLive"))) : (React.createElement(Text, { variant: "body2", className: classes.timeIndicator }, goesLiveAt)))),
            React.createElement("div", { className: `${classes.textContainer} cardTextContainer` },
                partnerTitle && (React.createElement(Text, { variant: "h5", className: `${classes.cardText} partnerTitle ${props.instructorLayout ? "small" : ""}` }, partnerTitle)),
                React.createElement(Text, { variant: "h3", className: `${classes.cardText} title ${props.instructorLayout ? "small" : ""}`, style: {
                        wordSpacing: props.instructorLayout ? "9999rem" : "",
                        width: props.instructorLayout ? "100%" : ""
                    } }, title),
                instructorName && (React.createElement(Text, { variant: "h5", className: `${classes.cardText} instructorName` }, instructorName)),
                React.createElement("div", { className: classes.descriptionAndRating, style: { marginTop: props.instructorLayout ? "0px" : "10px" } },
                    playButton && wasLiveAt && (React.createElement(Box, { display: "flex", alignItems: "center" },
                        playButton,
                        React.createElement(Text, { variant: "h5", className: `${classes.cardText} date` }, wasLiveAt))),
                    noOfFollowers && (React.createElement("div", { className: classes.followers },
                        React.createElement("div", null, formatNumber(noOfFollowers)),
                        React.createElement(Text, { variant: "h5" }, t("followers")))),
                    props.bookedUsersSummary && (React.createElement(UserAvatarListCards, { total: props.bookedUsersSummary.total, users: props.bookedUsersSummary.highlighted, noOfVisible: 5 })),
                    props.rating > 0 && (React.createElement("div", { className: classes.rating },
                        props.rating,
                        " ",
                        React.createElement(StarIcon, { className: classes.ratingIcon })))),
                (props.isFinished || props.isCancelled) && React.createElement("div", { className: classes.finishedOverlay })),
            toggleFavourite && (React.createElement(IconButton, { className: classes.favouriteButton, size: "small", disableRipple: true, disableTouchRipple: true, disableFocusRipple: true, TouchRippleProps: {}, onClickCapture: (e) => {
                    e.preventDefault();
                    toggleFavourite();
                } }, isLiked ? (React.createElement(KvixIcon.Heart.Filled, { className: classes.heartIcon })) : (React.createElement(KvixIcon.Heart.Empty, { className: classes.heartIcon })))),
            props.progress && (React.createElement(LinearProgress, { color: "primary", variant: "determinate", className: classes.progressBar, value: props.progress > 100 ? 100 : props.progress })))));
};
//# sourceMappingURL=CardBase.js.map